import { CREATE_TEAM_DRAWER_ID, NONE_OPTION_VALUE } from '../../constants';
import {
  CreateTeamFormData,
  CreateTeamSubmitPayload,
  OptionalModifierFieldConfig,
  OptionalModifierValue,
  StoredCreateTeamFormData,
} from './types';

function getDefaultModifierValue(
  field: OptionalModifierFieldConfig,
): OptionalModifierValue {
  switch (field.type) {
    case 'checkbox':
      return false;
    case 'dropdown':
      return NONE_OPTION_VALUE;
    case 'text':
    default:
      return '';
  }
}

export function buildDefaultOptionalModifiers(
  config: OptionalModifierFieldConfig[],
): Record<string, OptionalModifierValue> {
  return Object.fromEntries(
    config.map(field => [field.id, getDefaultModifierValue(field)]),
  );
}

export function getDefaultCreateTeamFormData(
  config: OptionalModifierFieldConfig[],
): CreateTeamFormData {
  return {
    name: '',
    applications: [],
    parentTeamId: '',
    account: NONE_OPTION_VALUE,
    locationIds: [],
    optionalModifiers: buildDefaultOptionalModifiers(config),
  };
}

export function serializeCreateTeamFormData(
  data: CreateTeamFormData,
): StoredCreateTeamFormData {
  return data;
}

export function deserializeCreateTeamFormData(
  data: StoredCreateTeamFormData | null | undefined,
  config: OptionalModifierFieldConfig[],
): CreateTeamFormData {
  const defaults = getDefaultCreateTeamFormData(config);

  if (!data) {
    return defaults;
  }

  return {
    ...defaults,
    name: data.name ?? '',
    applications: data.applications ?? [],
    parentTeamId: data.parentTeamId ?? '',
    account: data.account ?? NONE_OPTION_VALUE,
    locationIds: data.locationIds ?? [],
    optionalModifiers: {
      ...defaults.optionalModifiers,
      ...data.optionalModifiers,
    },
  };
}

export function normalizeOptionalModifiers(
  config: OptionalModifierFieldConfig[],
  values: Record<string, OptionalModifierValue>,
): Record<string, OptionalModifierValue> {
  const result: Record<string, OptionalModifierValue> = {};

  config.forEach(field => {
    const value = values[field.id];

    if (field.type === 'checkbox') {
      if (value === true) {
        result[field.id] = true;
      }
      return;
    }

    if (field.type === 'dropdown') {
      if (typeof value === 'string' && value && value !== NONE_OPTION_VALUE) {
        result[field.id] = value;
      }
      return;
    }

    const text = typeof value === 'string' ? value.trim() : '';
    if (text) {
      result[field.id] = text;
    }
  });

  return result;
}

export function buildCreateTeamSubmitPayload(
  data: CreateTeamFormData,
  config: OptionalModifierFieldConfig[],
): CreateTeamSubmitPayload {
  return {
    name: data.name.trim(),
    applications: data.applications,
    parentTeamId: data.parentTeamId,
    account: data.account === NONE_OPTION_VALUE ? null : data.account,
    locationIds: data.locationIds,
    optionalModifiers: normalizeOptionalModifiers(
      config,
      data.optionalModifiers,
    ),
  };
}

export function isCreateTeamDrawerRestore(id: string | undefined): boolean {
  return id === CREATE_TEAM_DRAWER_ID;
}
